import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Progress } from './ui/progress';
import { Upload, CheckCircle, Loader2, Music, AlertTriangle, Search, XCircle } from 'lucide-react';

const UploadProgressModal = ({ open, files, onOpenChange }) => {
  if (!files || files.length === 0) return null;

  const totalProgress = files.reduce((acc, file) => acc + file.progress, 0) / files.length;
  const completedCount = files.filter(f => f.status === 'completed').length;
  const skippedCount = files.filter(f => f.status === 'skipped').length;
  const uploadingFile = files.find(f => f.status === 'uploading');
  const checkingFile = files.find(f => f.status === 'checking');
  const allDone = completedCount + skippedCount === files.length;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-emerald-500" />;
      case 'uploading':
        return <Loader2 className="w-5 h-5 text-violet-600 animate-spin" />;
      case 'checking':
        return <Search className="w-5 h-5 text-blue-500 animate-pulse" />;
      case 'duplicate':
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case 'skipped':
        return <XCircle className="w-5 h-5 text-gray-400" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Music className="w-5 h-5 text-violet-500" />;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-emerald-50 border-emerald-200';
      case 'uploading':
        return 'bg-violet-50 border-violet-300 shadow-md';
      case 'checking':
        return 'bg-blue-50 border-blue-200';
      case 'duplicate':
        return 'bg-amber-50 border-amber-200';
      case 'skipped':
        return 'bg-gray-50 border-gray-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const getStatusText = (file) => {
    switch (file.status) {
      case 'completed':
        return '✓ Upload terminé';
      case 'uploading':
        return 'Upload en cours...';
      case 'checking':
        return '🔍 Vérification des doublons...';
      case 'duplicate':
        return '⚠️ Doublon détecté - En attente de décision';
      case 'skipped':
        return '↷ Ignoré (doublon)';
      case 'error':
        return `✗ Erreur: ${file.error}`;
      default:
        return 'En attente...';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="bg-white border-gray-200 text-gray-900 max-w-2xl"
        aria-describedby="upload-progress-description"
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-violet-100 border border-violet-200 flex items-center justify-center">
              {allDone ? (
                <CheckCircle className="w-6 h-6 text-emerald-500" />
              ) : (
                <Upload className="w-6 h-6 text-violet-600 animate-bounce" />
              )}
            </div>
            <DialogTitle className="text-xl text-gray-900">
              {allDone ? 'Upload terminé !' : 'Upload en cours...'}
            </DialogTitle>
          </div>
          <p id="upload-progress-description" className="sr-only">
            Progression de l&apos;upload des fichiers audio
          </p>
        </DialogHeader>

        <div className="space-y-6 mt-6">
          {/* Global Progress */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 font-medium">
                Progression ({completedCount}/{files.length} uploadés
                {skippedCount > 0 && `, ${skippedCount} ignorés`})
              </span>
              <span className="text-violet-600 font-mono font-semibold">
                {Math.round(totalProgress)}%
              </span>
            </div>
            <Progress value={totalProgress} className="h-3" />
          </div>

          {/* Files List */}
          <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
            {files.map((file, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg border transition-all ${getStatusClass(file.status)}`}
              >
                <div className="flex items-start gap-3">
                  {/* Icon */}
                  <div className="mt-1">
                    {getStatusIcon(file.status)}
                  </div>

                  {/* File Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <p className={`text-sm font-medium truncate ${
                        file.status === 'skipped' ? 'text-gray-400 line-through' : 'text-gray-900'
                      }`}>
                        {file.name}
                      </p>
                      <span className="text-xs font-mono text-violet-600 ml-2">
                        {file.status === 'completed' ? '100%' : `${file.progress}%`}
                      </span>
                    </div>

                    {/* Progress bar for individual file */}
                    {!['completed', 'skipped', 'duplicate'].includes(file.status) && (
                      <Progress 
                        value={file.progress} 
                        className="h-2"
                      />
                    )}

                    {/* Status message */}
                    <p className={`text-xs mt-2 ${
                      file.status === 'completed' ? 'text-emerald-600' :
                      file.status === 'error' ? 'text-red-600' :
                      file.status === 'duplicate' ? 'text-amber-600' :
                      file.status === 'skipped' ? 'text-gray-500' :
                      'text-gray-500'
                    }`}>
                      {getStatusText(file)}
                    </p>

                    {/* File size */}
                    {file.size && file.status !== 'skipped' && (
                      <p className="text-xs text-gray-400 mt-1">
                        {formatFileSize(file.size)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Current file being processed */}
          {(uploadingFile || checkingFile) && !allDone && (
            <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                {checkingFile ? (
                  <Search className="w-5 h-5 text-blue-500 animate-pulse" />
                ) : (
                  <Loader2 className="w-5 h-5 text-violet-600 animate-spin" />
                )}
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {checkingFile 
                      ? `Vérification : ${checkingFile.name}`
                      : `Upload en cours : ${uploadingFile?.name}`
                    }
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {checkingFile 
                      ? 'Recherche de doublons...'
                      : `${uploadingFile?.progress}% - Ne fermez pas cette fenêtre`
                    }
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Success message */}
          {allDone && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
              <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
              <p className="text-emerald-700 font-medium">
                Traitement terminé !
              </p>
              <p className="text-emerald-600 text-sm mt-1">
                {completedCount} fichier{completedCount > 1 ? 's' : ''} ajouté{completedCount > 1 ? 's' : ''}
                {skippedCount > 0 && `, ${skippedCount} ignoré${skippedCount > 1 ? 's' : ''}`}
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

export default UploadProgressModal;
